import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import rolesService from "../services/rolesService";
import styles from "../assets/roles.module.css";

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const response = await rolesService.getRoles();

      if (response.success) {
        setRoles(response.data);
      } else {
        throw new Error(response.message || 'Failed to load roles');
      }
    } catch (error) {
      console.error("Error loading roles:", error);
      toast.error("Failed to load roles. Using mock data.");

      // Fallback to mock data
      setRoles(rolesService.getMockRolesData());
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return "⇅";
    }
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  const getSortedRoles = () => {
    let sortedRoles = [...roles];

    if (sortConfig.key) {
      sortedRoles.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle permissions count sorting
        if (sortConfig.key === "permissions") {
          aValue = a.permissions?.length || 0;
          bValue = b.permissions?.length || 0;
        } else if (typeof aValue === "string") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return sortedRoles;
  };

  if (loading && roles.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.row}>
          <div className={styles.colMd12}>
            <div className={styles.panel + ' ' + styles.panelDefault + ' ' + styles.panelStandard}>
              <div className={styles.panelBody + ' ' + styles.textCenter}>
                <div className={styles.spinnerBorder} role="status">
                  <span className={styles.srOnly}>Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumb */}
      <nav className={styles.breadcrumb}>
        <Link to="/dashboard" className={styles.breadcrumbItem}>
          Dashboard
        </Link>
        <span> {' / '} </span>
        <span className={styles.menuHighlight}>Menu</span>
        <span> {' / '} </span>
        <span className={styles.breadcrumbActive}>Roles</span>
      </nav>

      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={styles.panel + ' ' + styles.panelDefault + ' ' + styles.panelStandard}>
            <div className={styles.panelHeading + ' ' + styles.panelTitle}>
              <h3>System Roles</h3>
            </div>
            <div className={styles.panelBody}>
              <table className={styles.table + ' ' + styles.tableRoles}>
                <thead>
                  <tr>
                    <th
                      onClick={() => handleSort("id")}
                      style={{ cursor: "pointer" }}
                    >
                      # <span style={{ marginLeft: "4px" }}>{getSortIcon("id")}</span>
                    </th>
                    <th
                      style={{ width: '200px', cursor: "pointer" }}
                      onClick={() => handleSort("name")}
                    >
                      Name <span style={{ marginLeft: "4px" }}>{getSortIcon("name")}</span>
                    </th>
                    <th
                      onClick={() => handleSort("permissions")}
                      style={{ cursor: "pointer" }}
                    >
                      Permissions <span style={{ marginLeft: "4px" }}>{getSortIcon("permissions")}</span>
                    </th>
                    <th colSpan="2"></th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedRoles().map((role) => (
                    <tr key={role.id}>
                      <td>{role.id}</td>
                      <td style={{ fontSize: '14px', fontWeight: '600' }}>{role.name}</td>
                      <td>
                        <div className={styles.permissionsContainer}>
                          {role.permissions.map((permission) => (
                            <span key={permission.id} className={styles.label + ' ' + styles.labelInfo} title={permission.name}>
                              {permission.name}
                            </span>
                          ))}
                        </div>
                      </td>
                      {role.id !== 1 && (
                        <td>
                          <Link 
                            to={`/roles/${role.id}/edit`} 
                            className={styles.btn + ' ' + styles.btnHead + ' ' + styles.btnStandard}
                          >
                            <i className={styles.glyphicon + ' ' + styles.glyphiconEdit}></i> Edit
                          </Link>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Roles; 